import { ITelemetryEvent } from '@mecontrol/common';
import { ME } from '../utilities';

type EventWriter = (event: ITelemetryEvent[]) => void;
type EventWriterCondition = () => boolean;
type EventWriterContext = [EventWriter, EventWriterCondition, number];
type EventWriterCollection = Array<EventWriterContext>;

const writerCollection: EventWriterCollection = [];
const criticalEventsRegex = /ContentUpdate|PageAction|TrackedScenario/;

/* Direct refernce to the oneDSWriter. This is to be used in conjunction with the old writerCollection so that
 * both sources are logged to. oneDS goes into Geneva while the original collection will log through Vortex
 * into the old Cosmos. This will be removed in the future and oneDS will be the final location.
*/
let oneDSWriter: EventWriter;

let shouldBatch = true;
let flusherAttached = false;
let windowHasLoaded = document.readyState === "complete";
let queuedEvents: ITelemetryEvent[] = [];

!windowHasLoaded && window.addEventListener('load', () => {
    windowHasLoaded = true;
    flushEvents();
});

/** Control whether events should be batched or not */
export function batchEvents(batch: boolean): void {
    shouldBatch = batch;

    // If there are any events pending and we are no
    // longer batching, we flush the queue
    if (!shouldBatch && queuedEvents.length > 0) {
        flushEvents();
    }
}

/**
 * Add a new method to write/send events
 * @param context Context for event writer being added:
 *  - First element: the actual event writer
 *  - Second element: a condition, which will be tested agains to determine
 * whether the writer should be used or not
 *  - Third element: a priority number used to determine the order in which
 * to test all of the available writers' conditions. Lower values mean testing
 * the writer first
 */
export function addEventWriter(...context: EventWriterContext): void {
    writerCollection.push(context);
    writerCollection.sort((contextA, contextB) => {
        // Sort by ascending priority
        return contextA[2] - contextB[2];
    });
}

/**
 * Sets the OneDS writer to be used in conjunction with the writer collection.
 */
export function setOneDSWriter(writer: EventWriter): void {
    oneDSWriter = writer;
}

/**
 * Write the given event to Vortex
 * @param event The telemetry event that will be sent to Vortex
 * @param isCritical Indicates whether the event being written should be treated as critical
 */
export function writeEvent(event: ITelemetryEvent, isCritical?: boolean): void {

    let evName = event.partA.name.split('.').pop() as string;
    if (ME.Config.telEvs && ME.Config.telEvs.indexOf(evName) !== -1) {

        queuedEvents.push(event);

        if (shouldBatch) {
            // Ensure that, as long as we are batching, we have
            // the chance to flush on unload
            addFlusher();

            // Flush all batched events if the current event is critical.
            // We hold off until the window has loaded to do this though
            // Also, when 'isCritical' is set to false, we hold off from flushing
            if (windowHasLoaded && isCritical !== false && (isCritical === true || criticalEventsRegex.test(evName))) {
                // Flushing always involve an extra delay to catch any additional immediate events
                setTimeout(flushEvents, 0);
            }
        }
        else {
            flushEvents();
        }
    }
}

/** Flush the queue of events and send them to Vortex */
export function flushEvents(): void {
    // Remove event listener, since there is no longer a need to flush on unload
    window.removeEventListener('beforeunload', flushEvents, true);
    flusherAttached = false;

    if (queuedEvents.length > 0) {
        // Only write events when not in unit tests
        if (!ME.Config.isUT) {
            // Write to 1DS as the new standard.
            oneDSWriter(queuedEvents);

            // Additionally write to the old writer to ensure all Vortex data continues to flow to Cosmos until removed.
            let writer = selectWriter();
            writer(queuedEvents);
        }
        queuedEvents = [];
    }
}

/** FOR TESTING ONLY. Clears all event writers */
export const clearWriters = () => { writerCollection.splice(0); };

/**
 * Determine which event writer to use when flushing events to Vortex
 */
function selectWriter(): EventWriter {
    // To select an event writer, test the condition of each
    // one in order of priority until one returns true
    for (let i = 0; i < writerCollection.length; i++) {
        // Test writer condition
        if (writerCollection[i][1]()) {
            // Return the actual writer
            return writerCollection[i][0];
        }
    }

    // If no conditions return true, we return a dummy writer
    return () => { /* NOOP */ };
}

/**
 * Add event listener in case we navigate away from the page before flushing
 * NOTE: Per this document, 'beforeunload' is the better event to handle when navigating away
 * (and only when strictly needed):
 *  - https://developers.google.com/web/updates/2018/07/page-lifecycle-api#the-beforeunload-event
 */
function addFlusher(): void {
    if (!flusherAttached) {
        flusherAttached = true;
        window.addEventListener('beforeunload', flushEvents, true);
    }
}
